/**
 * ATL-815 — mint ids for the elements of a `constants/<name>.data` array that lack one.
 *
 * Extracted from `EditableListScreen` so the property that matters can be tested without booting the
 * UI Kitten render tree: **N id-less elements must receive N DISTINCT ids.**
 *
 * The predecessor called `Date.now()` once per iteration inside a synchronous loop, so every id-less
 * element in a pass received the SAME id. That is ATL-800's root cause — not a race between two user
 * actions, a guaranteed collision for any org with two or more id-less items — and it is why Charge
 * lab 2.0 carried three distinct spending categories (`Вода`, `Чай / кава`, `Цукор`) all under
 * `spendingCategories1779208415099`.
 *
 * A duplicate id breaks both consumers, in different ways:
 *
 *  - `migrateConstants` upserts the array in ONE statement and Postgres refuses a statement whose
 *    proposed rows collide on the conflict key (`21000`) — zero rows land, so one duplicated id
 *    wiped the entire constant for that org.
 *  - `syncEditableConstantToSupabase` keys its create/update/delete diff on `new Map(items.map(i =>
 *    [i.id, i]))`. A Map collapses duplicates to one entry, so the diff is computed against the
 *    wrong set.
 *
 * ## Why the suffix is `${timestamp}-${index}` and not something random
 *
 * `dedupe-legacy-constant-rows-atl799.ts` — the only script in the repo that DELETES constants rows
 * — classifies an id as a deletable synthetic legacy id when the part after the prefix matches
 * `/^\d+-(0?\.\d+|\d+e-\d+)$/`: a timestamp, a hyphen, then a DECIMAL. That is the shape the old
 * pre-ATL-799 fallback produced with `Math.random()`. `1754…-0` does not match it (an integer is
 * neither `0?\.\d+` nor `\d+e-\d+`), so ids minted here are never mistaken for synthetics.
 *
 * ## Why this mutates its input, deliberately
 *
 * `EditableListScreen` holds `originalData` as `useState(() => [...loadedData])` — a SHALLOW copy,
 * sharing element objects with `data`. `syncEditableConstantToSupabase` then diffs the two by
 * `item.id`. Mutating the elements in place is what keeps both views agreeing on the minted id; hand
 * `data` fresh objects and leave `originalData` on the originals and every item reads as absent from
 * the original, so the diff classifies the whole list as `toCreate` and writes a duplicate Supabase
 * row per item on every save. Fixing that properly means re-deriving `originalData` as well, which
 * is a separate change — until then this mutation is load-bearing, not an oversight.
 *
 * @param items     the `data` array; elements lacking an `id` are mutated in place
 * @param prefix    the constants doc name (`spendingCategories`, `writeOffReasons`, …), or ''
 * @param mintedAt  a single timestamp for the whole pass — the caller reads the clock ONCE
 * @returns true when at least one id was minted
 */
export const assignMissingConstantIds = (items, prefix, mintedAt) => {
  if (!Array.isArray(items)) return false
  let minted = false
  for (let index = 0; index < items.length; index++) {
    const element = items[index]
    if (element && !element.id) {
      element.id = `${prefix ?? ''}${mintedAt}-${index}`
      minted = true
    }
  }
  return minted
}
